import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { GST_PARTS, type BillGst, type TdsBasis } from '../../../../shared/contracts/payment-taxes.js';
import type { Detail } from './DetailList.js';

/**
 * How the payment screens put a bill's taxes into words, so the voucher and
 * the advice read the same: the bill's GST line by line, whether it is paid
 * to the vendor or by the company, and why the TDS is what it is.
 */
export function usePaymentTaxText(): {
  readonly gstItems: (gst: BillGst) => Detail[];
  readonly gstNote: (gst: BillGst) => string;
  readonly tdsBasis: (tds: TdsBasis, base?: number) => string;
} {
  const { t } = useTranslation();
  const { money } = useBusinessNumber();

  const gstItems = (gst: BillGst): Detail[] => [
    { label: t('finance.tax.taxable'), value: money(gst.taxable), testId: 'tax-taxable' },
    ...GST_PARTS.filter((p) => gst[p] !== 0).map((p) => ({
      label: t(`finance.tax.${p}`),
      value: money(gst[p]),
      testId: `tax-${p}`,
    })),
    ...(gst.reverseCharge
      ? [{ label: t('finance.tax.lessReverseCharge'), value: `\u2212${money(gst.reverseCharge)}`, testId: 'tax-reverse-charge' }]
      : []),
  ];

  const gstNote = (gst: BillGst): string =>
    gst.total === 0
      ? t('finance.tax.noGst')
      : gst.reverseCharge
        ? t('finance.tax.reverseNote', { amount: money(gst.reverseCharge) })
        : t('finance.tax.forwardNote', { amount: money(gst.charged) });

  const tdsBasis = (tds: TdsBasis, base?: number): string => {
    if (tds.tdsAmount === 0) {
      const single = tds.singlePaymentThreshold;
      const year = tds.annualAggregateThreshold;
      const vars = {
        section: tds.sectionCode,
        taxable: money(tds.taxable),
        single: single === null ? '' : money(single),
        year: money(tds.priorAggregate + tds.taxable),
        aggregate: year === null ? '' : money(year),
      };
      return single !== null && year !== null
        ? t('finance.tax.tdsWithinBoth', vars)
        : single !== null
          ? t('finance.tax.tdsWithinSingle', vars)
          : t('finance.tax.tdsWithinYear', vars);
    }
    if (tds.certificate) {
      return t('finance.tax.tdsWithCertificate', {
        section: tds.sectionCode,
        certificate: tds.certificate.certificateNo,
        certRate: String(tds.certificate.ratePct),
        rate: String(tds.applicableRatePct),
      });
    }
    return base === undefined
      ? t('finance.tax.tdsAtRate', { section: tds.sectionCode, rate: String(tds.applicableRatePct) })
      : t('finance.tax.tdsAtRateOn', { section: tds.sectionCode, rate: String(tds.applicableRatePct), base: money(base) });
  };

  return { gstItems, gstNote, tdsBasis };
}
